import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";

export const Route = createFileRoute("/en/company/type/$type")({
  loader: async ({ params }) => {
    const { COMPANY_ENTITY_TYPES, getCompanyEntityType } = await import("@/content/company-corpus/company-types");
    const t = getCompanyEntityType(params.type);
    if (!t) throw notFound();
    return { t, siblings: COMPANY_ENTITY_TYPES.filter((x) => x.slug !== t.slug).map((x) => ({ slug: x.slug, label: x.titleEn })) };
  },
  component: CompanyTypePageEn,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Page not found" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { t } = loaderData;
    const faqs = [
      { q: `${t.titleEn} — who is it for?`, a: t.whoEn },
      { q: "What is the filing sequence?", a: t.routeEn },
      { q: "Which authority decides?", a: t.authorityEn },
      ...t.pitfallsEn.slice(0, 3).map((p) => ({ q: "What gets applications rejected?", a: p })),
    ];
    return {
      ...pageHeadForProvince({
        title: `${t.titleEn} — Conditions, Documents and Process`,
        description: t.whoEn.slice(0, 155),
        path: `/en/company/type/${params.type}`,
        type: "article",
        alternates: [{ lang: "th", path: `/company/type/${params.type}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Business Entity Types", url: `${site}/en/company/type` },
              { name: t.titleEn, url: `${site}/en/company/type/${t.slug}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildHowToStepsSchema({ name: `How to register a ${t.titleEn}`, description: t.routeEn, path: `/en/company/type/${params.type}`, steps: t.processEn, inLanguage: "en" })) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/company/type/${params.type}`)) },
      ],
    };
  },
});

function List({ title, items }: { title: string; items: string[] }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 text-xl font-bold">{title}</h2>
      <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
        {items.map((x: string, i: number) => <li key={i}>{x}</li>)}
      </ul>
    </section>
  );
}

function CompanyTypePageEn() {
  const { t, siblings } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/company/type" className="hover:underline">Business Entity Types</a> <span>/</span> <span>{t.titleEn}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{t.titleEn}</h1>
        <p className="mb-6 text-muted-foreground">{t.whoEn}</p>
        <div className="rounded-lg border bg-muted/30 p-5">
          <h2 className="mb-2 text-lg font-bold">Filing sequence at a glance</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{t.routeEn}</p>
        </div>
        <List title="Conditions to meet" items={t.requirementsEn} />
        <List title="Documents to prepare" items={t.documentsEn} />
        <section className="mt-8">
          <h2 className="mb-3 text-xl font-bold">Step-by-step process</h2>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            {t.processEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
        </section>
        <List title="Common mistakes" items={t.pitfallsEn} />
        <section className="mt-8">
          <h2 className="mb-2 text-xl font-bold">Deciding authority</h2>
          <p className="text-sm text-muted-foreground">{t.authorityEn}</p>
        </section>
        <section className="mt-8 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Talk to our team</h2>
          <p className="text-sm text-muted-foreground">
            Ask about scope of work, required documents and timing by phone, LINE or email. Approvals are at the
            authority's discretion — we do not guarantee outcomes and we do not set up nominee structures, but we
            review your file before submission to reduce the risk of rejection.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <section className="mt-8">
          <h2 className="mb-2 text-base font-bold">Sources</h2>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            {t.sources.map((s: { label: string; url: string }) => (
              <li key={s.url}><a href={s.url} rel="nofollow noopener" target="_blank" className="underline">{s.label}</a></li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            Information current as of August 2026 — reconfirm the latest conditions with the authority before filing.
          </p>
        </section>
        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Other structures</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((o: { slug: string; label: string }) => (
              <a key={o.slug} href={`/en/company/type/${o.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {o.label}
              </a>
            ))}
          </div>
          <p className="mt-4 text-sm">
            More: <a href="/en/company/faq" className="underline hover:text-gold">Company registration FAQ</a> ·{" "}
            <a href="/en/work-permit/type" className="underline hover:text-gold">Work permit routes</a> ·{" "}
            <a href="/en/visa/thai" className="underline hover:text-gold">Thai visa types</a>
          </p>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}